import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, FileText, Wrench, AlertTriangle, Calendar, Upload, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import CanAccess from '@/components/ui/CanAccess';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import FilterBar from '@/components/ui/FilterBar';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import ChartCard from '@/components/ui/ChartCard';
import Modal from '@/components/ui/Modal';
import { ChartSkeleton } from '@/components/ui/LoadingSkeleton';
import ProgressBar from '@/components/ui/ProgressBar';
import { format, differenceInDays, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay, parseISO } from 'date-fns';
import { useTableExport } from '@/hooks/useTableExport';

interface Equipment {
  id: string;
  equipmentCode: string;
  name: string;
  type: string;
  lab: { name: string; labCode: string };
  status: string;
  calibrationDue: string;
  maintenanceDue: string;
  utilizationRate: number;
  manufacturer: string;
  model: string;
  serialNo: string;
  installDate: string;
  certificates?: { name: string; uploadedAt: string; uploadedBy: string }[];
}

interface MaintenanceEvent {
  id: string;
  equipmentId: string;
  equipmentName: string;
  serviceType: string;
  scheduledDate: string;
  technician: string;
  estimatedDuration: number;
  notes: string;
  status?: string;
}

const EQUIPMENT_TYPES = ['HPLC', 'GC', 'Spectrophotometer', 'Balance', 'Autoclave', 'Centrifuge', 'pH Meter', 'Viscometer'];
const SERVICE_TYPES = [
  { value: 'calibration', label: 'Calibration' },
  { value: 'preventive-maintenance', label: 'Preventive Maintenance' },
  { value: 'repair', label: 'Repair' },
  { value: 'inspection', label: 'Inspection' },
];

function getDotColor(dateStr: string): string {
  const daysUntil = differenceInDays(new Date(dateStr), new Date());
  if (daysUntil < 0) return '#DC2626';       // red — overdue
  if (daysUntil <= 7) return '#D97706';       // amber — due soon
  return '#16A34A';                           // green — upcoming
}

export default function Equipment() {
  const [activeTab, setActiveTab] = useState<'list' | 'schedule'>('list');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [labId, setLabId] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEquip, setSelectedEquip] = useState<Equipment | null>(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<Equipment | null>(null);
  const [scheduleForm, setScheduleForm] = useState({
    serviceType: 'calibration',
    scheduledDate: '',
    technician: '',
    estimatedDuration: '',
    notes: '',
  });
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [certFile, setCertFile] = useState<File | null>(null);
  const certInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    name: '', type: '', labId: '', manufacturer: '', model: '',
    serialNo: '', installDate: '', calibrationDue: '', maintenanceDue: '',
  });
  const queryClient = useQueryClient();

  const exportColumns = [
    { key: 'equipCode', label: 'Equip ID' },
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'calibrationDue', label: 'Calibration Due' },
    { key: 'utilizationRate', label: 'Utilization %' },
  ];
  const { data, isLoading } = useQuery({
    queryKey: ['equipment', { search, type: typeFilter, status: statusFilter, page, labId }],
    queryFn: () =>
      api.get('/equipment', { params: { search, type: typeFilter, status: statusFilter, page, limit: 20, ...(labId && { labId }) } })
        .then((r) => r.data),
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
  });

  const { data: utilisationData, isLoading: utilisLoading } = useQuery({
    queryKey: ['equipment-utilisation'],
    queryFn: () => api.get('/equipment/utilisation').then((r) => {
      const grouped = r.data.data;
      if (!grouped || typeof grouped !== 'object' || Array.isArray(grouped)) return [];
      return Object.entries(grouped).map(([type, quarters]) => {
        const vals = Object.values(quarters as Record<string, number>);
        const avg = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
        return { type, avg: Math.round(avg * 10) / 10 };
      });
    }),
    staleTime: 300000,
  });

  const { data: matrixData } = useQuery({
    queryKey: ['equipment-matrix'],
    queryFn: () => api.get('/equipment/matrix').then((r) => {
      const d = r.data.data;
      if (!d) return { types: [], rows: [] };
      return {
        types: Array.isArray(d.types) ? d.types : [],
        rows: Array.isArray(d.matrix)
          ? d.matrix.map((m: { lab: { name: string }; types: Record<string, boolean> }) => ({
              lab: m.lab.name,
              counts: Object.fromEntries(Object.entries(m.types).map(([k, v]) => [k, v ? 1 : 0])),
            }))
          : [],
      };
    }),
    staleTime: 300000,
  });

  const { data: scheduleData } = useQuery({
    queryKey: ['equipment-schedule'],
    queryFn: () =>
      api.get('/equipment/maintenance-schedule').then((r) => r.data.data).catch(() => []),
    staleTime: 300000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/equipment', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      setShowCreate(false);
      setForm({ name: '', type: '', labId: '', manufacturer: '', model: '', serialNo: '', installDate: '', calibrationDue: '', maintenanceDue: '' });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: ({ id, ...formData }: { id: string } & typeof scheduleForm) =>
      api.post(`/equipment/${id}/maintenance`, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-schedule'] });
      setShowSchedule(false);
      setScheduleForm({ serviceType: 'calibration', scheduledDate: '', technician: '', estimatedDuration: '', notes: '' });
    },
  });

  const uploadCertMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append('certificate', file);
      return api.post(`/equipment/${id}/certificates`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      setCertFile(null);
      if (certInputRef.current) certInputRef.current.value = '';
    },
  });

  // Calibration due within 30 days helper
  function daysUntilCalibration(dateStr: string): number {
    return differenceInDays(new Date(dateStr), new Date());
  }

  const { exportCSV, exportPDF } = useTableExport(data?.data ?? [], exportColumns, 'equipment-list');

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'equipmentCode', label: 'ID', width: 'w-28',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.equipmentCode)}</span> },
    { key: 'name', label: 'Equipment',
      render: (row) => {
        const calDue = String(row.calibrationDue);
        const days = daysUntilCalibration(calDue);
        const warnCalib = days >= 0 && days <= 30;
        return (
          <div className="flex items-center gap-2">
            <div>
              <p className="font-medium text-text text-sm">{String(row.name)}</p>
              <p className="text-xs text-text-dim">{String(row.type)} · {String(row.manufacturer || '—')}</p>
            </div>
            {warnCalib && (
              <span title={`Calibration due in ${days}d`}>
                <AlertTriangle size={13} className="text-amber-500 flex-shrink-0" />
              </span>
            )}
          </div>
        );
      }},
    { key: 'lab', label: 'Lab',
      render: (row) => {
        const lab = row.lab as { name: string; labCode: string } | null;
        return (
          <div>
            <p className="text-sm text-text">{lab?.name || '—'}</p>
            <p className="text-xs font-mono text-text-dim">{lab?.labCode}</p>
          </div>
        );
      }},
    { key: 'status', label: 'Status', render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'calibrationDue', label: 'Calibration Due',
      render: (row) => {
        const dateStr = String(row.calibrationDue);
        const date = new Date(dateStr);
        const days = daysUntilCalibration(dateStr);
        const overdue = days < 0;
        const dueSoon = days >= 0 && days <= 30;
        return (
          <div className="flex flex-col gap-0.5">
            <p className={`text-xs font-mono ${overdue ? 'text-risk-high font-semibold' : 'text-text'}`}>
              {format(date, 'd MMM yyyy')}
            </p>
            {overdue && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200 w-fit">
                OVERDUE
              </span>
            )}
            {dueSoon && !overdue && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 w-fit">
                {days}d remaining
              </span>
            )}
          </div>
        );
      }},
    { key: 'utilizationRate', label: 'Utilisation', align: 'center',
      render: (row) => {
        const val = Number(row.utilizationRate);
        return (
          <div className="w-20">
            <div className="flex justify-between text-[10px] mb-0.5">
              <span className="text-text-dim">{val}%</span>
            </div>
            <ProgressBar value={val} size="sm" />
          </div>
        );
      }},
    { key: '_actions', label: '',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setScheduleTarget(row as unknown as Equipment);
            setShowSchedule(true);
          }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-surface border border-border text-text-muted hover:text-text hover:border-teal/40 transition-colors"
        >
          <Wrench size={11} /> Schedule PM
        </button>
      )},
  ];

  // ─── Calendar helpers ──────────────────────────────────────────
  const calEvents: MaintenanceEvent[] = scheduleData || [];
  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPadding = getDay(monthStart); // 0=Sun
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function eventsOnDay(day: Date): MaintenanceEvent[] {
    return calEvents.filter((ev) => {
      try { return isSameDay(parseISO(ev.scheduledDate), day); } catch { return false; }
    });
  }

  const upcomingEvents = [...calEvents]
    .filter((ev) => {
      try { return new Date(ev.scheduledDate) >= new Date(); } catch { return false; }
    })
    .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
    .slice(0, 15);

  const UTIL_COLORS = ['#b07d1a', '#c9922a', '#a07218', '#F0D080', '#99F6E4', '#CCFBF1', 'rgba(176,125,26,0.08)', '#D1FAE5'];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Equipment & Capabilities"
        subtitle={`${data?.meta?.total || 0} equipment items tracked`}
        actions={
          <>
            <button onClick={exportCSV} style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:8, background:'#F0FDF4', border:'1px solid #16A34A30', color:'#16A34A', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              <Download size={13} /> CSV
            </button>
            <button onClick={exportPDF} style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:8, background:'#EFF6FF', border:'1px solid #2563EB30', color:'#2563EB', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              <FileText size={13} /> PDF
            </button>
            <CanAccess roles={['admin', 'qa_director', 'lab_head']}>
              <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> Add Equipment</button>
            </CanAccess>
          </>
        }
      />

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 p-1 bg-surface border border-border rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('list')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'list'
              ? 'bg-white text-text shadow-sm border border-border'
              : 'text-text-muted hover:text-text'
          }`}
        >
          <Wrench size={13} /> Equipment List
        </button>
        <button
          onClick={() => setActiveTab('schedule')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'schedule'
              ? 'bg-white text-text shadow-sm border border-border'
              : 'text-text-muted hover:text-text'
          }`}
        >
          <Calendar size={13} /> Maintenance Schedule
        </button>
      </div>

      <FilterBar
        labId={labId} onLabChange={(id) => { setLabId(id); setPage(1); }}
        showDateFilter={false}
      />

      {/* ─── EQUIPMENT LIST TAB ─── */}
      {activeTab === 'list' && (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {utilisLoading ? <ChartSkeleton height={200} /> : (
              <ChartCard title="Utilisation by Type" subtitle="Quarterly average %" height={200}>
                <ResponsiveContainer>
                  <BarChart data={Array.isArray(utilisationData) ? utilisationData : []} margin={{ top: 5, right: 20, bottom: 5, left: 0 }} barSize={18}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                    <XAxis dataKey="type" tick={{ fontSize: 9, fill: '#6899A0' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={30} domain={[0, 100]} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => [`${v}%`, 'Utilisation']} />
                    <Bar dataKey="avg" name="Avg %" radius={[3, 3, 0, 0]}>
                      {(Array.isArray(utilisationData) ? utilisationData : []).map((_: unknown, i: number) => (
                        <Cell key={i} fill={UTIL_COLORS[i % UTIL_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            <ChartCard title="Lab × Capability Matrix" subtitle="Equipment count per lab" height={200}>
              <div className="overflow-auto h-full">
                {matrixData && (
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr>
                        <th className="text-left text-text-muted font-medium pb-2 pr-3">Lab</th>
                        {(Array.isArray(matrixData.types) ? matrixData.types : []).map((t: string) => (
                          <th key={t} className="text-center text-text-muted font-medium pb-2 px-1.5 whitespace-nowrap">{t}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(matrixData.rows) ? matrixData.rows : []).map((row: { lab: string; counts: Record<string, number> }) => (
                        <tr key={row.lab} className="border-t border-border/50">
                          <td className="py-1.5 pr-3 text-text font-medium whitespace-nowrap">{row.lab}</td>
                          {(Array.isArray(matrixData.types) ? matrixData.types : []).map((t: string) => {
                            const count = row.counts[t] || 0;
                            return (
                              <td key={t} className="text-center py-1.5 px-1.5">
                                {count > 0 ? (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-teal/10 text-teal font-semibold">{count}</span>
                                ) : (
                                  <span className="text-border">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </ChartCard>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
              <input placeholder="Search equipment..." value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
            </div>
            <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="select w-40">
              <option value="">All Types</option>
              {EQUIPMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
              <option value="">All Status</option>
              <option value="operational">Operational</option>
              <option value="maintenance">Maintenance</option>
              <option value="calibration_due">Calibration Due</option>
              <option value="offline">Offline</option>
            </select>
          </div>

          <DataTable
            columns={columns}
            data={(data?.data || []) as Record<string, unknown>[]}
            loading={isLoading}
            onRowClick={(row) => setSelectedEquip(row as unknown as Equipment)}
            total={data?.meta?.total}
            page={page}
            pageSize={20}
            onPageChange={setPage}
          />
        </>
      )}

      {/* ─── MAINTENANCE SCHEDULE TAB ─── */}
      {activeTab === 'schedule' && (
        <div className="space-y-5">
          {/* Calendar */}
          <div className="bg-surface border border-border rounded-xl p-5">
            {/* Month nav */}
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setCalendarMonth((m) => subMonths(m, 1))}
                className="p-1.5 rounded-lg hover:bg-bg transition-colors">
                <ChevronLeft size={16} className="text-text-muted" />
              </button>
              <h3 className="text-sm font-semibold text-text">{format(calendarMonth, 'MMMM yyyy')}</h3>
              <button onClick={() => setCalendarMonth((m) => addMonths(m, 1))}
                className="p-1.5 rounded-lg hover:bg-bg transition-colors">
                <ChevronRight size={16} className="text-text-muted" />
              </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center text-[10px] font-semibold text-text-muted py-1">{d}</div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
              {/* padding cells */}
              {Array.from({ length: startPadding }).map((_, i) => (
                <div key={`pad-${i}`} className="bg-bg/40 min-h-[72px]" />
              ))}
              {daysInMonth.map((day) => {
                const events = eventsOnDay(day);
                const isToday = isSameDay(day, new Date());
                return (
                  <div key={day.toISOString()}
                    className={`bg-surface min-h-[72px] p-1.5 ${isToday ? 'ring-2 ring-inset ring-teal/40' : ''}`}
                  >
                    <p className={`text-[11px] font-medium mb-1 w-5 h-5 flex items-center justify-center rounded-full
                      ${isToday ? 'bg-teal text-white' : 'text-text-muted'}`}>
                      {format(day, 'd')}
                    </p>
                    <div className="flex flex-wrap gap-0.5">
                      {events.map((ev, idx) => (
                        <span
                          key={idx}
                          title={`${ev.equipmentName} — ${ev.serviceType}`}
                          style={{ background: getDotColor(ev.scheduledDate) }}
                          className="w-2 h-2 rounded-full flex-shrink-0"
                        />
                      ))}
                    </div>
                    {events.length > 0 && (
                      <p className="text-[9px] text-text-dim mt-0.5 leading-tight truncate">
                        {events[0].equipmentName}
                        {events.length > 1 && ` +${events.length - 1}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-3">
              {[
                { color: '#DC2626', label: 'Overdue' },
                { color: '#D97706', label: 'Due within 7 days' },
                { color: '#16A34A', label: 'Upcoming' },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                  <span className="text-[11px] text-text-muted">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming maintenance list */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-text">Upcoming Maintenance</h3>
              <p className="text-xs text-text-muted">Sorted by scheduled date</p>
            </div>
            {upcomingEvents.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-text-muted">No upcoming maintenance events scheduled.</div>
            ) : (
              <div className="divide-y divide-border">
                {upcomingEvents.map((ev, i) => {
                  const daysAway = differenceInDays(new Date(ev.scheduledDate), new Date());
                  return (
                    <div key={ev.id || i} className="flex items-center gap-4 px-5 py-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-bg flex flex-col items-center justify-center">
                        <p className="text-[10px] font-medium text-text-muted leading-none">{format(new Date(ev.scheduledDate), 'MMM')}</p>
                        <p className="text-sm font-bold text-text leading-none">{format(new Date(ev.scheduledDate), 'd')}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text truncate">{ev.equipmentName}</p>
                        <p className="text-xs text-text-muted">{ev.serviceType} {ev.technician ? `· ${ev.technician}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {ev.estimatedDuration && (
                          <span className="flex items-center gap-1 text-[11px] text-text-muted">
                            <Clock size={10} /> {ev.estimatedDuration}h
                          </span>
                        )}
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            daysAway <= 7 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
                          }`}
                        >
                          {daysAway === 0 ? 'Today' : `in ${daysAway}d`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Create Equipment Modal ─── */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Equipment"
        subtitle="Register new lab equipment" size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Adding...' : 'Add Equipment'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Equipment Name *</label>
            <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Waters HPLC System" />
          </div>
          <div>
            <label className="label">Type *</label>
            <select className="select" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
              <option value="">Select type...</option>
              {EQUIPMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Lab *</label>
            <select className="select" value={form.labId} onChange={(e) => setForm((f) => ({ ...f, labId: e.target.value }))}>
              <option value="">Select lab...</option>
              {(Array.isArray(labs) ? labs : []).map((l: { id: string; name: string }) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Manufacturer</label>
            <input className="input" value={form.manufacturer} onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))} placeholder="e.g. Waters" />
          </div>
          <div>
            <label className="label">Model</label>
            <input className="input" value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} placeholder="e.g. ACQUITY UPLC" />
          </div>
          <div>
            <label className="label">Serial No.</label>
            <input className="input" value={form.serialNo} onChange={(e) => setForm((f) => ({ ...f, serialNo: e.target.value }))} placeholder="Serial number..." />
          </div>
          <div>
            <label className="label">Install Date</label>
            <input type="date" className="input" value={form.installDate} onChange={(e) => setForm((f) => ({ ...f, installDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Calibration Due *</label>
            <input type="date" className="input" value={form.calibrationDue} onChange={(e) => setForm((f) => ({ ...f, calibrationDue: e.target.value }))} />
          </div>
          <div>
            <label className="label">Maintenance Due</label>
            <input type="date" className="input" value={form.maintenanceDue} onChange={(e) => setForm((f) => ({ ...f, maintenanceDue: e.target.value }))} />
          </div>
        </div>
      </Modal>

      {/* ─── Schedule PM Modal ─── */}
      <Modal
        isOpen={showSchedule}
        onClose={() => { setShowSchedule(false); setScheduleTarget(null); }}
        title="Schedule Preventive Maintenance"
        subtitle={scheduleTarget ? `${scheduleTarget.name} · ${scheduleTarget.equipmentCode}` : 'Select equipment'}
        size="md"
        footer={
          <>
            <button onClick={() => { setShowSchedule(false); setScheduleTarget(null); }} className="btn-secondary">Cancel</button>
            <button
              onClick={() => {
                if (scheduleTarget) scheduleMutation.mutate({ id: scheduleTarget.id, ...scheduleForm });
              }}
              disabled={scheduleMutation.isPending || !scheduleTarget || !scheduleForm.scheduledDate}
              className="btn-primary"
            >
              {scheduleMutation.isPending ? 'Scheduling...' : 'Schedule PM'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Service Type *</label>
            <select className="select" value={scheduleForm.serviceType}
              onChange={(e) => setScheduleForm((f) => ({ ...f, serviceType: e.target.value }))}>
              {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Scheduled Date *</label>
              <input type="date" className="input" value={scheduleForm.scheduledDate}
                onChange={(e) => setScheduleForm((f) => ({ ...f, scheduledDate: e.target.value }))} />
            </div>
            <div>
              <label className="label">Est. Duration (hours)</label>
              <input type="number" min="0.5" step="0.5" className="input" value={scheduleForm.estimatedDuration}
                onChange={(e) => setScheduleForm((f) => ({ ...f, estimatedDuration: e.target.value }))} placeholder="e.g. 2" />
            </div>
          </div>
          <div>
            <label className="label">Technician</label>
            <input className="input" value={scheduleForm.technician}
              onChange={(e) => setScheduleForm((f) => ({ ...f, technician: e.target.value }))} placeholder="Technician name..." />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={3} value={scheduleForm.notes}
              onChange={(e) => setScheduleForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Additional notes or instructions..." />
          </div>
        </div>
      </Modal>

      {/* ─── Equipment Detail Modal ─── */}
      <Modal isOpen={!!selectedEquip} onClose={() => setSelectedEquip(null)}
        title={selectedEquip?.name || ''} subtitle={selectedEquip?.equipmentCode} size="lg">
        {selectedEquip && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Type', value: selectedEquip.type },
                { label: 'Status', value: <PillBadge value={selectedEquip.status} showDot /> },
                { label: 'Lab', value: selectedEquip.lab?.name },
                { label: 'Manufacturer', value: selectedEquip.manufacturer || '—' },
                { label: 'Model', value: selectedEquip.model || '—' },
                { label: 'Serial No.', value: selectedEquip.serialNo || '—' },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-bg rounded-lg">
                  <p className="text-xs text-text-muted mb-1">{item.label}</p>
                  {typeof item.value === 'string'
                    ? <p className="text-sm font-medium text-text">{item.value}</p>
                    : item.value}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted mb-1">Calibration Due</p>
                <p className={`text-sm font-mono font-semibold ${new Date(selectedEquip.calibrationDue) < new Date() ? 'text-risk-high' : 'text-text'}`}>
                  {format(new Date(selectedEquip.calibrationDue), 'd MMM yyyy')}
                </p>
              </div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted mb-1">Utilisation</p>
                <p className="text-sm font-mono font-semibold text-text">{selectedEquip.utilizationRate}%</p>
                <ProgressBar value={selectedEquip.utilizationRate} size="sm" />
              </div>
            </div>
            {new Date(selectedEquip.calibrationDue) < new Date() && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-risk-high/10 border border-risk-high/20">
                <AlertTriangle size={14} className="text-risk-high flex-shrink-0" />
                <p className="text-xs text-risk-high font-medium">Calibration overdue — equipment may need to be taken offline pending recalibration.</p>
              </div>
            )}

            {/* Schedule PM shortcut */}
            <div className="flex justify-end">
              <button
                onClick={() => { setScheduleTarget(selectedEquip); setSelectedEquip(null); setShowSchedule(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal/10 text-teal border border-teal/20 hover:bg-teal/20 transition-colors"
              >
                <Wrench size={13} /> Schedule PM
              </button>
            </div>

            {/* Calibration Certificates */}
            <div className="border-t border-border pt-4">
              <p className="text-sm font-semibold text-text mb-3 flex items-center gap-1.5">
                <FileText size={14} className="text-text-muted" /> Calibration Certificates
              </p>

              {/* Existing certificates */}
              {(selectedEquip.certificates || []).length > 0 ? (
                <div className="space-y-1.5 mb-3">
                  {(selectedEquip.certificates || []).map((cert, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 bg-bg rounded-lg text-xs">
                      <div className="flex items-center gap-2">
                        <FileText size={12} className="text-text-muted flex-shrink-0" />
                        <div>
                          <p className="font-medium text-text">{cert.name}</p>
                          <p className="text-text-muted">{cert.uploadedBy} · {format(new Date(cert.uploadedAt), 'd MMM yyyy')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-text-muted mb-3">No certificates uploaded yet.</p>
              )}

              {/* Upload section */}
              <div className="flex items-center gap-3">
                <input
                  ref={certInputRef}
                  type="file"
                  accept=".pdf,.jpg,.png"
                  className="text-xs text-text-muted file:mr-2 file:py-1 file:px-3 file:rounded file:border file:border-border file:text-xs file:bg-surface file:text-text-muted hover:file:bg-bg cursor-pointer flex-1"
                  onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
                />
                <button
                  disabled={!certFile || uploadCertMutation.isPending}
                  onClick={() => { if (certFile) uploadCertMutation.mutate({ id: selectedEquip.id, file: certFile }); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface border border-border text-text hover:border-teal/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  <Upload size={12} />
                  {uploadCertMutation.isPending ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
